// src/pages/AuthPage.jsx
import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'

const INP = {
  background: '#141920', border: '1px solid #1e2535', borderRadius: '10px',
  color: '#e2e8f0', padding: '12px 16px', fontSize: '14px', outline: 'none',
  fontFamily: "'Inter',sans-serif", width: '100%', boxSizing: 'border-box',
  transition: 'border-color 0.2s',
}

export default function AuthPage() {
  const { signInWithGoogle, signInWithEmail, signUpWithEmail, resetPassword } = useAuth()
  const [mode,     setMode]     = useState('login')   // login | register | forgot
  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [confirm,  setConfirm]  = useState('')
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState('')
  const [success,  setSuccess]  = useState('')

  const reset = () => { setError(''); setSuccess('') }

  const handleSubmit = async () => {
    reset()
    if (!email.trim()) return setError('Email wajib diisi')

    if (mode === 'forgot') {
      setLoading(true)
      try {
        await resetPassword(email)
        setSuccess('Link reset password sudah dikirim ke email kamu!')
      } catch (e) { setError(e.message) } finally { setLoading(false) }
      return
    }

    if (!password) return setError('Password wajib diisi')
    if (mode === 'register' && password !== confirm) return setError('Password tidak cocok')
    if (mode === 'register' && password.length < 6)  return setError('Password minimal 6 karakter')

    setLoading(true)
    try {
      if (mode === 'login')    await signInWithEmail(email, password)
      if (mode === 'register') {
        await signUpWithEmail(email, password)
        setSuccess('Registrasi berhasil! Cek email kamu untuk verifikasi.')
        setMode('login')
      }
    } catch (e) {
      const msg = e.message.includes('Invalid login') ? 'Email atau password salah'
        : e.message.includes('already registered')    ? 'Email sudah terdaftar'
        : e.message
      setError(msg)
    } finally { setLoading(false) }
  }

  const handleGoogle = async () => {
    reset()
    setLoading(true)
    try { await signInWithGoogle() }
    catch (e) { setError(e.message); setLoading(false) }
  }

  return (
    <div style={{ minHeight: '100vh', background: '#080b10', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: '20px',
      fontFamily: "'Inter',sans-serif" }}>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Inter:wght@400;500;600;700&display=swap');
        *{box-sizing:border-box}
        input:focus{border-color:#00ff9d60!important}
      `}</style>

      <div style={{ width: '100%', maxWidth: '400px' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '14px',
            background: 'rgba(0,255,157,0.12)', border: '1px solid #00ff9d40',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '26px', margin: '0 auto 14px' }}>⚡</div>
          <div style={{ color: '#00ff9d', fontFamily: "'Space Mono',monospace",
            fontWeight: '700', fontSize: '22px', letterSpacing: '0.05em' }}>LEVIARCH</div>
          <div style={{ color: '#555', fontSize: '12px', fontFamily: "'Space Mono',monospace",
            marginTop: '4px' }}>AIRDROP TRACKER</div>
        </div>

        {/* Card */}
        <div style={{ background: '#0e1117', border: '1px solid #1e2535',
          borderRadius: '20px', padding: '32px',
          boxShadow: '0 0 60px rgba(0,255,157,0.05)' }}>

          <h2 style={{ color: '#e2e8f0', fontFamily: "'Space Mono',monospace",
            fontSize: '16px', margin: '0 0 24px', textAlign: 'center' }}>
            {mode === 'login'    ? 'Masuk ke Akunmu'    :
             mode === 'register' ? 'Buat Akun Baru'     : 'Reset Password'}
          </h2>

          {/* Error / Success */}
          {error && (
            <div style={{ background: 'rgba(255,77,109,0.1)', border: '1px solid rgba(255,77,109,0.3)',
              borderRadius: '10px', padding: '10px 14px', color: '#ff4d6d',
              fontSize: '13px', marginBottom: '16px' }}>⚠️ {error}</div>
          )}
          {success && (
            <div style={{ background: 'rgba(0,255,157,0.08)', border: '1px solid rgba(0,255,157,0.3)',
              borderRadius: '10px', padding: '10px 14px', color: '#00ff9d',
              fontSize: '13px', marginBottom: '16px' }}>✅ {success}</div>
          )}

          {/* Google Button */}
          {mode !== 'forgot' && (
            <>
              <button onClick={handleGoogle} disabled={loading} style={{
                width: '100%', background: '#fff', border: '1px solid #ddd',
                borderRadius: '10px', color: '#333', padding: '12px',
                cursor: loading ? 'not-allowed' : 'pointer', fontSize: '14px',
                fontWeight: '600', display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: '10px', marginBottom: '20px',
                transition: 'opacity 0.2s', opacity: loading ? 0.7 : 1,
              }}>
                <svg width="18" height="18" viewBox="0 0 48 48">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                </svg>
                Masuk dengan Google
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <div style={{ flex: 1, height: '1px', background: '#1e2535' }}/>
                <span style={{ color: '#555', fontSize: '12px', fontFamily: "'Space Mono',monospace" }}>atau</span>
                <div style={{ flex: 1, height: '1px', background: '#1e2535' }}/>
              </div>
            </>
          )}

          {/* Email */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <input style={INP} type="email" placeholder="Email" value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()} />

            {mode !== 'forgot' && (
              <input style={INP} type="password" placeholder="Password" value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
            )}

            {mode === 'register' && (
              <input style={INP} type="password" placeholder="Konfirmasi Password" value={confirm}
                onChange={e => setConfirm(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
            )}

            <button onClick={handleSubmit} disabled={loading} style={{
              background: 'rgba(0,255,157,0.18)', border: '1px solid #00ff9d70',
              borderRadius: '10px', color: '#00ff9d', padding: '13px',
              cursor: loading ? 'not-allowed' : 'pointer', fontSize: '14px',
              fontFamily: "'Space Mono',monospace", fontWeight: '700',
              transition: 'opacity 0.2s', opacity: loading ? 0.7 : 1,
              marginTop: '4px',
            }}>
              {loading ? 'Loading...' :
               mode === 'login'    ? '→ Masuk' :
               mode === 'register' ? '→ Daftar' : '→ Kirim Link Reset'}
            </button>
          </div>

          {/* Footer links */}
          <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column',
            gap: '8px', alignItems: 'center' }}>
            {mode === 'login' && (
              <>
                <button onClick={() => { setMode('forgot'); reset() }} style={{
                  background: 'none', border: 'none', color: '#555', cursor: 'pointer',
                  fontSize: '12px', textDecoration: 'underline' }}>
                  Lupa password?
                </button>
                <span style={{ color: '#555', fontSize: '12px' }}>
                  Belum punya akun?{' '}
                  <button onClick={() => { setMode('register'); reset() }} style={{
                    background: 'none', border: 'none', color: '#00ff9d',
                    cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                    Daftar sekarang
                  </button>
                </span>
              </>
            )}
            {(mode === 'register' || mode === 'forgot') && (
              <button onClick={() => { setMode('login'); reset() }} style={{
                background: 'none', border: 'none', color: '#555', cursor: 'pointer',
                fontSize: '12px', textDecoration: 'underline' }}>
                ← Kembali ke Login
              </button>
            )}
          </div>
        </div>

        <p style={{ textAlign: 'center', color: '#333', fontSize: '11px',
          fontFamily: "'Space Mono',monospace", marginTop: '20px' }}>
          Data tersimpan aman di Supabase
        </p>
      </div>
    </div>
  )
}
